import Button from "components/ui/Button";
import UILabel from "components/ui/UILabel";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Balance from "./Balance";
import {
  CHECKING_BALANCE_START,
  EARNINGS_BALANCE_START,
  SLIDES,
  TRANSACTIONS,
} from "./constants";
import * as Styled from "./styled";
import Transaction from "./Transaction";

const CashflowPlus = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const navigate = useNavigate();

  const isCashflowActive = slideIndex === 1;
  const currentSlide = SLIDES[slideIndex];

  const checkingBalanceEnd = TRANSACTIONS.reduce((sum, item) => {
    if (!currentSlide.ewaTransactions.includes(item.id)) {
      return sum - item.amount;
    }

    return sum;
  }, CHECKING_BALANCE_START);

  const earningsBalanceEnd = TRANSACTIONS.reduce((sum, item) => {
    if (currentSlide.ewaTransactions.includes(item.id)) {
      return sum - item.amount;
    }

    return sum;
  }, EARNINGS_BALANCE_START);

  const navigateSlide = (direction = 1) => {
    setSlideIndex(
      (prev) => (prev + SLIDES.length + 1 * direction) % SLIDES.length
    );
  };

  const onClickNext = () => {
    if (slideIndex === SLIDES.length - 1) {
      navigate("/transaction-settings");
    } else {
      navigateSlide();
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    const action = (() => {
      switch (event.code) {
        case "ArrowLeft":
          return () => navigateSlide(-1);
        case "ArrowRight":
          return () => navigateSlide();
        default:
          return null;
      }
    })();

    if (action) {
      event.preventDefault();
      action();
    }
  };

  useEffect(() => {
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyUp]);

  return (
    <Styled.Wrapper $isCashflowActive={isCashflowActive}>
      <Styled.Header>
        <UILabel.Micro bold wideCaps color="disabled">
          How It Works
        </UILabel.Micro>
        <UILabel.Medium>{currentSlide.description}</UILabel.Medium>
      </Styled.Header>

      <Styled.Timeline>
        <Styled.TimelineBackground />
        <Styled.TimelineDivider />

        <Styled.SlideLabel>
          <UILabel.Micro bold wideCaps>
            {isCashflowActive ? "With Switchboard" : "Without Switchboard"}
          </UILabel.Micro>
        </Styled.SlideLabel>

        <Styled.StartBalances>
          <Balance amount={CHECKING_BALANCE_START} title="Checking balance" />
          <Balance
            amount={EARNINGS_BALANCE_START}
            title="Earnings balance"
            status={isCashflowActive ? "active" : undefined}
          />
        </Styled.StartBalances>

        <Styled.TransactionsList>
          {TRANSACTIONS.map((item) => {
            const isActive = currentSlide.ewaTransactions.includes(item.id);

            return (
              <Styled.TransactionWrapper key={item.id} isActive={isActive}>
                <motion.div
                  layout
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <Transaction {...item} isActive={isActive} />
                </motion.div>
              </Styled.TransactionWrapper>
            );
          })}

          {!isCashflowActive && (
            <Styled.EarningsDisabledMessage>
              <UILabel.Medium>
                Earnings aren't accessible until payday.
              </UILabel.Medium>
            </Styled.EarningsDisabledMessage>
          )}
        </Styled.TransactionsList>

        <Styled.EndBalances>
          <Balance
            amount={checkingBalanceEnd}
            title="Projected checking balance"
            status={isCashflowActive ? "success" : "warning"}
          />
          {isCashflowActive && (
            <Balance
              amount={earningsBalanceEnd}
              title="Projected earnings balance"
              status="active"
            />
          )}
        </Styled.EndBalances>
      </Styled.Timeline>

      <Button fullWidth onClick={onClickNext}>
        {slideIndex === SLIDES.length - 1 ? "Finish" : "Next"}
      </Button>
    </Styled.Wrapper>
  );
};

export default CashflowPlus;
